import produce from "immer";
import styles from "src/components/chart/legend.module.css";
import { ChartBounds } from "src/components/chart/bounds";
import {
  ChartScales,
  SCATTER_CHART_CIRCLE_OPACITY,
} from "src/components/chart/scales";
import { ChartSeries } from "src/components/chart/series";
import { Icon } from "src/components/icon/icon";
import { VisualChart } from "src/gql/visuals/types";
import { accessString } from "src/components/chart/scales";
import {
  chartOptionBubbleChart,
  chartOptionName,
} from "src/components/chart/options";
import { chartOptionOnlyPieCharts } from "src/components/chart/options";
import { chartOptionVisible } from "src/components/chart/options";
import { formatTitle } from "src/components/chart/format";
import { useTheme } from "src/components/theme/theme";
import { yAxisColumns } from "src/components/chart/series";
import { orderBy } from "lodash";

export const ChartLegend: React.FC<{
  visual: VisualChart;
  series: ChartSeries;
  bounds: ChartBounds;
  scales: ChartScales;
  setVisual: React.Dispatch<React.SetStateAction<VisualChart>>;
}> = (props) => {
  const theme = useTheme();
  const width = props.bounds.legend;
  const createCircleStyle = createCircleStyleFn(props);

  if (width === 0) {
    return null;
  }

  const buttonStyle = { color: theme.series_legend_color };

  const onToggle = (name: string) => () => {
    props.setVisual(
      produce((draft: VisualChart) => {
        const o = draft.options;
        const v = chartOptionVisible(props.visual, name);
        o.seriesOptions = o.seriesOptions ?? {};
        o.seriesOptions[name] = o.seriesOptions[name] ?? {};
        o.seriesOptions[name].visible = !v;
      })
    );
  };

  const onTogglePie = (name: string) => () => {
    props.setVisual(
      produce((draft: VisualChart) => {
        const o = draft.options;
        const v = chartOptionVisible(props.visual, name);
        o.valuesOptions = o.valuesOptions ?? {};
        o.valuesOptions[name] = o.valuesOptions[name] ?? {};
        o.valuesOptions[name].visible = !v;
      })
    );
  };

  if (chartOptionOnlyPieCharts(props.visual)) {
    // We sort the labels by decreasing value in the pie chart
    const sortedLegend = orderBy(
      props.series.rows,
      [props.series.ys[0].name],
      ["desc"]
    );

    return (
      <div className={styles.legend} style={{ width }}>
        <ul aria-label="Chart legend">
          {sortedLegend.map((r, i) => {
            const n = String(r[props.series.x.name]);
            const v = chartOptionVisible(props.visual, n);

            return (
              <li key={i}>
                <button
                  type="button"
                  aria-pressed={v}
                  onClick={onTogglePie(n)}
                  style={buttonStyle}
                >
                  {formatTitle(props.series.x, r[props.series.x.name])}
                </button>
                <span
                  style={createCircleStyle(accessString(props.series.x)(r))}
                >
                  <Icon icon="circle-fill" />
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // We reverse the order of the legend to match the order of the stacked series in the chart
  const orderedLegend = yAxisColumns(props.series).reverse();

  return (
    <div className={styles.legend} style={{ width }}>
      <ul aria-label="Chart legend">
        {orderedLegend.map((c) => {
          const n = c.name;
          const v = chartOptionVisible(props.visual, n);

          return (
            <li key={n}>
              <button
                type="button"
                aria-pressed={v}
                onClick={onToggle(n)}
                style={buttonStyle}
              >
                {chartOptionName(props.visual, n)}
              </button>
              <span style={createCircleStyle(n)}>
                <Icon icon="circle-fill" />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

interface CreateCircleStyleProps {
  visual: VisualChart;
  scales: ChartScales;
  series: ChartSeries;
}

export const createCircleStyleFn =
  ({ visual, scales, series }: CreateCircleStyleProps) =>
  (column: string) => {
    const style: React.CSSProperties = {
      color: scales.color(column),
    };

    const isScatterChartSeries = Boolean(
      series.y0.scatter.find(({ name }) => name === column)
    );

    if (chartOptionBubbleChart(visual, scales.z) && isScatterChartSeries) {
      style.opacity = SCATTER_CHART_CIRCLE_OPACITY;
    }

    return style;
  };
